// TanStack Query hooks for the group-chat opt-in toggle. Reads/writes via settingsApi
// (real or mock). Toggling invalidates both the opt-in state and the group room list,
// since opt-in governs whether the user sees any rooms at all (SPEC §2).
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { settingsApi } from "./settingsApi";

const OPTIN_KEY = ["group", "optIn"] as const;

export function useGroupChatOptIn() {
  return useQuery({
    queryKey: OPTIN_KEY,
    queryFn: () => settingsApi.getOptIn(),
  });
}

export function useSetGroupChatOptIn() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (optIn: boolean) => settingsApi.setOptIn(optIn),
    // Optimistic flip so the switch reacts instantly; roll back on error.
    onMutate: async (optIn) => {
      await qc.cancelQueries({ queryKey: OPTIN_KEY });
      const prev = qc.getQueryData<boolean>(OPTIN_KEY);
      qc.setQueryData(OPTIN_KEY, optIn);
      return { prev };
    },
    onError: (_err, _optIn, ctx) => {
      if (ctx?.prev !== undefined) qc.setQueryData(OPTIN_KEY, ctx.prev);
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: OPTIN_KEY });
      qc.invalidateQueries({ queryKey: ["group", "rooms"] });
    },
  });
}
